import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../App';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Phone, FileText, BarChart3, Settings, Plus, Play, Trash2, Edit2, Save, X, Users, Filter, ChevronDown, Square, Search, MapPin, UserCheck, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const VOICES = [
  { id: 'alloy', name: 'Alloy (нейтральный)' },
  { id: 'ash', name: 'Ash (мужской)' },
  { id: 'ballad', name: 'Ballad (мужской мягкий)' },
  { id: 'coral', name: 'Coral (женский)' },
  { id: 'echo', name: 'Echo (мужской)' },
  { id: 'sage', name: 'Sage (мужской)' },
  { id: 'shimmer', name: 'Shimmer (женский мягкий)' },
  { id: 'verse', name: 'Verse (мужской)' },
  { id: 'marin', name: 'Marin (женский)' },
  { id: 'cedar', name: 'Cedar (мужской низкий)' },
];

const STATUS_LABELS_RU = {
  new: 'Новый', ftd: 'ФТД', in_progress: 'В работе', potential: 'Потенциальный',
  not_potential: 'Не потенциальный', under_18: 'Нет 18', no_russian: 'Не говорит на рус',
  not_interested: 'Не интересно', no_answer: 'Не отвечает', replace: 'Заменить',
  duplicate: 'Дубль', fake: 'Фейк',
};

const CMultiSelect = ({ value, onChange, options, placeholder, testId }) => {
  const { tt } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const selected = Array.isArray(value) ? value : [];
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);
  const display = selected.length === 0 ? placeholder : selected.length === 1
    ? (options.find(o => (o.value || o) === selected[0])?.label || options.find(o => (o.value || o) === selected[0]) || selected[0])
    : `${tt('Выбрано')}: ${selected.length}`;
  return (
    <div className="relative">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className="w-full border rounded-lg px-2 py-1.5 text-sm text-left flex justify-between items-center bg-white" data-testid={testId}>
        <span className={selected.length === 0 ? 'text-gray-400 truncate' : 'truncate'}>{display}</span>
        <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 ml-1" />
      </button>
      {isOpen && (<>
        <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
        <div className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-52 overflow-y-auto">
          {options.map((opt, i) => {
            const val = opt.value !== undefined ? opt.value : opt;
            const label = opt.label || opt;
            const on = selected.includes(val);
            return (
              <div key={i} onClick={() => toggle(val)} className={`px-2.5 py-1.5 text-sm cursor-pointer flex items-center hover:bg-gray-50 ${on ? 'bg-blue-50 text-blue-700' : ''}`}>
                <div className={`w-3.5 h-3.5 mr-2 border rounded flex-shrink-0 flex items-center justify-center ${on ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                  {on && <Check className="w-2.5 h-2.5 text-white" />}
                </div>
                {label}
              </div>
            );
          })}
        </div>
      </>)}
    </div>
  );
};

export default function VoiceAgentPage() {
  const { user } = useContext(AuthContext);
  const { t, tt } = useLanguage();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const [tab, setTab] = useState('scripts');
  const [scripts, setScripts] = useState([]);
  const [stats, setStats] = useState({});
  const [settings, setSettings] = useState({});
  const [editScript, setEditScript] = useState(null);
  const [editSettings, setEditSettings] = useState(false);
  const [loading, setLoading] = useState(false);
  // Campaign call state
  const [showCampaign, setShowCampaign] = useState(false);
  const [campaignFilter, setCampaignFilter] = useState({ script_id: '', statuses: [], campaigns: [], countries: [], managers: [], ai_called: '', limit: 500 });
  const [campaignClients, setCampaignClients] = useState([]);
  const [campaignTotal, setCampaignTotal] = useState(0);
  const [campaignLoading, setCampaignLoading] = useState(false);
  const [campaignSearch, setCampaignSearch] = useState('');
  const [selectedClients, setSelectedClients] = useState(new Set());
  const [campaignActive, setCampaignActive] = useState(false);
  // SIP status
  const [sipStatus, setSipStatus] = useState(null);
  // Filter options from DB
  const [filterOptions, setFilterOptions] = useState({ statuses: [], campaigns: [] });
  // Guide modal
  const [showGuide, setShowGuide] = useState(false);
  // New profile creation
  const [creatingProfile, setCreatingProfile] = useState(false);
  const [newProfileForm, setNewProfileForm] = useState({ name: '' });

  // Fetch profiles
  const fetchProfiles = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/voice-agent/profiles`, { headers: getHeaders() });
      setProfiles(res.data);
      if (res.data.length > 0 && !selectedProfileId) {
        setSelectedProfileId(res.data[0].id);
      }
    } catch (e) { /* */ }
  }, [selectedProfileId]);

  // Fetch data for selected profile
  const fetchData = useCallback(async () => {
    if (!selectedProfileId) return;
    try {
      const pid = selectedProfileId;
      const [s, st, se, fo] = await Promise.all([
        axios.get(`${API}/voice-agent/scripts?profile_id=${pid}`, { headers: getHeaders() }),
        axios.get(`${API}/voice-agent/stats?profile_id=${pid}`, { headers: getHeaders() }),
        axios.get(`${API}/voice-agent/settings`, { headers: getHeaders() }),
        axios.get(`${API}/voice-agent/filter-options`, { headers: getHeaders() }),
      ]);
      setScripts(s.data);
      setStats(st.data);
      setSettings({...se.data, profiles});
      setFilterOptions(fo.data);
    } catch (e) { /* */ }
  }, [selectedProfileId, profiles]);

  const checkSip = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/voice-agent/sip-status`, { headers: getHeaders() });
      setSipStatus(res.data);
    } catch (e) { setSipStatus({ connected: false, error: 'Ошибка проверки' }); }
  }, []);

  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);
  useEffect(() => { fetchData(); checkSip(); }, [fetchData, checkSip]);

  const handleCreateProfile = async () => {
    if (!newProfileForm.name) return toast.error(tt('Введите имя агента'));
    try {
      const res = await axios.post(`${API}/voice-agent/profiles`, newProfileForm, { headers: getHeaders() });
      setCreatingProfile(false);
      setNewProfileForm({ name: '' });
      await fetchProfiles();
      setSelectedProfileId(res.data.id);
      setTab('settings');
      toast.success(tt('Агент создан! Настройте SIP подключение.'));
    } catch (e) { toast.error(e.response?.data?.detail || 'Ошибка'); }
  };

  // --- Scripts Tab ---
  const ScriptsTab = () => {
    const [form, setForm] = useState(editScript || {
      name: '', agent_name: '', agent_role: '', company_name: '',
      voice: 'alloy', greeting: '', system_prompt: '', qualification_questions: [''], max_duration_seconds: 300, is_active: true, profile_id: selectedProfileId,
    });

    const handleSave = async () => {
      try {
        const payload = { ...form, qualification_questions: form.qualification_questions.filter(q => q.trim()) };
        if (editScript?.id) {
          await axios.put(`${API}/voice-agent/scripts/${editScript.id}`, payload, { headers: getHeaders() });
        } else {
          await axios.post(`${API}/voice-agent/scripts`, payload, { headers: getHeaders() });
        }
        setEditScript(null);
        fetchData();
      } catch (e) { toast.error(e.response?.data?.detail || 'Ошибка'); }
    };

    const handleDelete = async (id) => {
      if (!window.confirm('Удалить скрипт?')) return;
      await axios.delete(`${API}/voice-agent/scripts/${id}`, { headers: getHeaders() });
      fetchData();
    };

    if (editScript !== null) {
      return (
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold">{editScript?.id ? 'Редактировать скрипт' : 'Новый скрипт'}</h3>
            <button onClick={() => setEditScript(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-600">{tt('Название скрипта')}</label>
              <input className="w-full border rounded-lg px-3 py-2 mt-1" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder={tt('Квалификация КЗ')} />
            </div>
            <div>
              <label className="text-sm text-gray-600">{tt('Голос AI')}</label>
              <select className="w-full border rounded-lg px-3 py-2 mt-1" value={form.voice} onChange={e => setForm({...form, voice: e.target.value})}>
                {VOICES.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-600">{tt('Имя агента')}</label>
              <input className="w-full border rounded-lg px-3 py-2 mt-1" value={form.agent_name} onChange={e => setForm({...form, agent_name: e.target.value})} />
            </div>
            <div>
              <label className="text-sm text-gray-600">{tt('Должность агента')}</label>
              <input className="w-full border rounded-lg px-3 py-2 mt-1" value={form.agent_role} onChange={e => setForm({...form, agent_role: e.target.value})} />
            </div>
            <div>
              <label className="text-sm text-gray-600">{tt('Компания')}</label>
              <input className="w-full border rounded-lg px-3 py-2 mt-1" value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} />
            </div>
            <div>
              <label className="text-sm text-gray-600">{tt('Макс. длительность (сек)')}</label>
              <input type="number" className="w-full border rounded-lg px-3 py-2 mt-1" value={form.max_duration_seconds} onChange={e => setForm({...form, max_duration_seconds: parseInt(e.target.value) || 300})} />
            </div>
          </div>
          <div className="mt-4">
            <label className="text-sm text-gray-600">{tt('Приветствие (первая фраза AI)')}</label>
            <textarea className="w-full border rounded-lg px-3 py-2 mt-1 h-20" value={form.greeting} onChange={e => setForm({...form, greeting: e.target.value})} placeholder={tt('Здравствуйте! Это Амир...')} />
          </div>
          <div className="mt-4">
            <label className="text-sm text-gray-600">{tt('Дополнительные инструкции (системный промпт)')}</label>
            <textarea className="w-full border rounded-lg px-3 py-2 mt-1 h-24" value={form.system_prompt} onChange={e => setForm({...form, system_prompt: e.target.value})} placeholder={tt('Особые инструкции для AI...')} />
          </div>
          <div className="mt-4">
            <label className="text-sm text-gray-600 mb-2 block">{tt('Вопросы для квалификации')}</label>
            {(form.qualification_questions || ['']).map((q, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input className="flex-1 border rounded-lg px-3 py-2" value={q} onChange={e => {
                  const qs = [...form.qualification_questions];
                  qs[i] = e.target.value;
                  setForm({...form, qualification_questions: qs});
                }} placeholder={`Вопрос ${i+1}`} />
                <button onClick={() => {
                  const qs = form.qualification_questions.filter((_, idx) => idx !== i);
                  setForm({...form, qualification_questions: qs.length ? qs : ['']});
                }} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button onClick={() => setForm({...form, qualification_questions: [...(form.qualification_questions || []), '']})} className="text-sm text-blue-600 hover:text-blue-800">{tt('+ Добавить вопрос')}</button>
          </div>
          <div className="mt-6 flex gap-3">
            <button onClick={handleSave} className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"><Save className="w-4 h-4" /> {tt('Сохранить')}</button>
            <button onClick={() => setEditScript(null)} className="bg-gray-100 text-gray-600 px-6 py-2 rounded-lg hover:bg-gray-200">{tt('Отмена')}</button>
          </div>
        </div>
      );
    }

    return (
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">{tt('Скрипты диалогов')}</h3>
          <button onClick={() => setEditScript({})} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm"><Plus className="w-4 h-4" /> {tt('Новый скрипт')}</button>
        </div>
        {scripts.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center text-gray-400">{tt('Нет скриптов. Создайте первый.')}</div>
        ) : (
          <div className="space-y-3">
            {scripts.map(s => (
              <div key={s.id} className="bg-white rounded-xl p-4 shadow-sm flex justify-between items-center">
                <div>
                  <div className="font-medium text-gray-900">{s.name}</div>
                  <div className="text-sm text-gray-500">{s.agent_name} • {VOICES.find(v=>v.id===s.voice)?.name || s.voice} • {s.qualification_questions?.length || 0} вопросов</div>
                  <div className="text-xs text-gray-400 mt-1">Звонков: {s.total_calls || 0} • Успешных: {s.successful_calls || 0}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setEditScript(s)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"><Edit2 className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(s.id)} className="p-2 text-red-400 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // --- Calls Tab ---
  const CallsTab = () => {
    const [callsData, setCallsData] = useState({ items: [], total: 0 });
    const [page, setPage] = useState(0);
    const [perPage] = useState(15);
    const [statusFilter, setStatusFilter] = useState('');
    const [resultFilter, setResultFilter] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('initiated_at');
    const [sortDir, setSortDir] = useState(-1);
    const [callsLoading, setCallsLoading] = useState(false);

    const outcomeColors = { interested: 'text-green-600 bg-green-50', callback: 'text-yellow-600 bg-yellow-50', not_interested: 'text-red-600 bg-red-50', failed: 'text-gray-600 bg-gray-100', cancelled: 'text-orange-600 bg-orange-50', no_answer: 'text-gray-500 bg-gray-50', queued: 'text-blue-600 bg-blue-50', initiating: 'text-indigo-600 bg-indigo-50', connecting: 'text-purple-600 bg-purple-50', paused: 'text-yellow-700 bg-yellow-100', unknown: 'text-gray-400 bg-gray-50' };
    const outcomeLabels = { interested: 'Заинтересован', callback: 'Перезвонить', not_interested: 'Отказ', no_answer: 'Не ответил', failed: 'Ошибка', cancelled: 'Отменён', unknown: 'Неизвестно', initiating: 'Набор...', connecting: 'Соединение...', queued: 'В очереди', completed: 'Завершён', paused: 'На паузе' };

    const fetchCalls = useCallback(async () => {
      setCallsLoading(true);
      try {
        const params = new URLSearchParams({ limit: perPage, skip: page * perPage, sort_by: sortBy, sort_dir: sortDir });
        if (selectedProfileId) params.set('profile_id', selectedProfileId);
        if (statusFilter) params.set('status', statusFilter);
        if (resultFilter) params.set('result', resultFilter);
        if (searchQuery) params.set('search', searchQuery);
        const res = await axios.get(`${API}/voice-agent/calls?${params}`, { headers: getHeaders() });
        setCallsData(res.data);
      } catch (e) { /* */ }
      setCallsLoading(false);
    }, [page, perPage, statusFilter, resultFilter, searchQuery, sortBy, sortDir]);

    useEffect(() => { fetchCalls(); }, [fetchCalls]);

    // Auto-refresh: always poll every 5s while on calls tab, check for stale statuses
    useEffect(() => {
      const interval = setInterval(fetchCalls, 5000);
      return () => clearInterval(interval);
    }, [fetchCalls]);

    const hasActive = callsData.items.some(c => ['queued', 'initiating', 'connecting'].includes(c.status));
    const hasPaused = callsData.items.some(c => c.status === 'paused');
    const totalPages = Math.ceil(callsData.total / perPage);

    const handleSort = (field) => {
      if (sortBy === field) { setSortDir(d => d === -1 ? 1 : -1); }
      else { setSortBy(field); setSortDir(-1); }
      setPage(0);
    };

    const SortIcon = ({ field }) => {
      if (sortBy !== field) return <ChevronDown className="w-3 h-3 text-gray-300" />;
      return <ChevronDown className={`w-3 h-3 text-blue-600 transition-transform ${sortDir === 1 ? 'rotate-180' : ''}`} />;
    };

    const handleStop = async () => {
      try {
        const res = await axios.post(`${API}/voice-agent/campaign-stop`, {}, { headers: getHeaders() });
        toast.success(`Остановлено: ${res.data.cancelled} звонков`);
        fetchCalls();
      } catch (e) { toast.error(tt('Ошибка остановки')); }
    };

    const handlePause = async () => {
      try {
        const res = await axios.post(`${API}/voice-agent/campaign-pause`, {}, { headers: getHeaders() });
        toast.success(`Приостановлено: ${res.data.paused} звонков`);
        fetchCalls();
      } catch (e) { toast.error(tt('Ошибка паузы')); }
    };

    const handleResume = async () => {
      try {
        const res = await axios.post(`${API}/voice-agent/campaign-resume`, {}, { headers: getHeaders() });
        toast.success(`Возобновлено: ${res.data.resumed} звонков`);
        fetchCalls();
      } catch (e) { toast.error(tt('Ошибка возобновления')); }
    };

    const handleRecall = async (call) => {
      try {
        await axios.post(`${API}/voice-agent/calls/initiate`, {
          client_id: call.client_id,
          script_id: String(call.script_id),
          phone_override: call.client_phone
        }, { headers: getHeaders() });
        fetchCalls();
      } catch (e) {
        const msg = e.response?.data?.detail || 'Ошибка звонка';
        toast.error(msg);
      }
    };

    return (
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold" data-testid="calls-title">{tt('История звонков')} <span className="text-sm font-normal text-gray-400">({callsData.total})</span></h3>
          <div className="flex gap-2">
            {hasActive && (
              <>
                <button onClick={handlePause} className="bg-yellow-500 text-white px-3 py-2 rounded-lg hover:bg-yellow-600 flex items-center gap-1.5 text-sm" data-testid="pause-campaign-btn">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg> {tt('Пауза')}
                </button>
                <button onClick={handleStop} className="bg-red-600 text-white px-3 py-2 rounded-lg hover:bg-red-700 flex items-center gap-1.5 text-sm" data-testid="stop-campaign-btn"><Square className="w-4 h-4" /> {tt('Стоп')}</button>
              </>
            )}
            {hasPaused && (
              <button onClick={handleResume} className="bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 flex items-center gap-1.5 text-sm" data-testid="resume-campaign-btn">
                <Play className="w-4 h-4" /> Продолжить ({callsData.items.filter(c => c.status === 'paused').length})
              </button>
            )}
            <button onClick={() => setShowCampaign(true)} className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-1.5 text-sm" data-testid="mass-call-btn"><Phone className="w-4 h-4" /> {tt('Обзвон')}</button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-3 shadow-sm mb-3 flex flex-wrap gap-2 items-center" data-testid="calls-filters">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
            <input className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm" placeholder={tt('Поиск по имени/телефону...')}
              value={searchQuery} onChange={e => { setSearchQuery(e.target.value); setPage(0); }} data-testid="calls-search" />
          </div>
          <select className="border rounded-lg px-3 py-2 text-sm" value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(0); }} data-testid="calls-status-filter">
            <option value="">{tt('Все статусы')}</option>
            <option value="completed">{tt('Завершён')}</option>
            <option value="failed">{tt('Ошибка')}</option>
            <option value="cancelled">{tt('Отменён')}</option>
            <option value="queued">{tt('В очереди')}</option>
            <option value="paused">{tt('На паузе')}</option>
          </select>
          <select className="border rounded-lg px-3 py-2 text-sm" value={resultFilter}
            onChange={e => { setResultFilter(e.target.value); setPage(0); }} data-testid="calls-result-filter">
            <option value="">{tt('Все результаты')}</option>
            <option value="interested">{tt('Заинтересован')}</option>
            <option value="callback">{tt('Перезвонить')}</option>
            <option value="not_interested">{tt('Отказ')}</option>
            <option value="no_answer">{tt('Не ответил')}</option>
            <option value="unknown">{tt('Неизвестно')}</option>
          </select>
          {(statusFilter || resultFilter || searchQuery) && (
            <button onClick={() => { setStatusFilter(''); setResultFilter(''); setSearchQuery(''); setPage(0); }}
              className="text-xs text-red-500 hover:text-red-700 px-2 py-1" data-testid="calls-clear-filters">{tt('Сбросить')}</button>
          )}
        </div>

        {callsData.items.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center text-gray-400">{callsLoading ? 'Загрузка...' : 'Нет звонков'}</div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="calls-table">
                <thead><tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
                  <th className="px-3 py-3 cursor-pointer select-none" onClick={() => handleSort('initiated_at')}>
                    <span className="flex items-center gap-1">{tt('Дата')} <SortIcon field="initiated_at" /></span></th>
                  <th className="px-3 py-3">ID</th>
                  <th className="px-3 py-3">{tt('Клиент')}</th>
                  <th className="px-3 py-3">{tt('Телефон')}</th>
                  <th className="px-3 py-3">{tt('Скрипт')}</th>
                  <th className="px-3 py-3 cursor-pointer select-none" onClick={() => handleSort('duration_seconds')}>
                    <span className="flex items-center gap-1">{tt('Длит.')} <SortIcon field="duration_seconds" /></span></th>
                  <th className="px-3 py-3 cursor-pointer select-none" onClick={() => handleSort('result')}>
                    <span className="flex items-center gap-1">{tt('Результат')} <SortIcon field="result" /></span></th>
                  <th className="px-3 py-3">{tt('Данные')}</th>
                  <th className="px-3 py-3"></th>
                </tr></thead>
                <tbody>
                  {callsData.items.map(c => (
                    <tr key={c.id} className={`border-t hover:bg-gray-50 cursor-pointer ${c.status === 'queued' ? 'bg-yellow-50' : c.status === 'paused' ? 'bg-yellow-50/50' : ''}`}
                      onClick={() => c.client_id && navigate(`/crm/clients/${c.client_id}`)}
                      data-testid={`call-row-${c.id}`}>
                      <td className="px-3 py-2 text-xs whitespace-nowrap">{c.initiated_at ? new Date(c.initiated_at).toLocaleString('ru') : '-'}</td>
                      <td className="px-3 py-2 text-xs text-gray-400 font-mono">{c.client_id}</td>
                      <td className="px-3 py-2 text-sm font-medium">{c.client_name}</td>
                      <td className="px-3 py-2 text-xs text-gray-500 font-mono">{c.client_phone}</td>
                      <td className="px-3 py-2 text-xs text-gray-600">{c.script_name}</td>
                      <td className="px-3 py-2 text-xs">{c.duration_seconds ? `${Math.floor(c.duration_seconds/60)}:${(c.duration_seconds%60).toString().padStart(2,'0')}` : '-'}</td>
                      <td className="px-3 py-2"><span className={`text-xs px-2 py-0.5 rounded-full ${outcomeColors[c.result] || outcomeColors[c.status] || outcomeColors.unknown}`}>{outcomeLabels[c.result] || outcomeLabels[c.status] || c.result || c.status}</span></td>
                      <td className="px-3 py-2 text-xs text-gray-500 max-w-[200px] truncate">{c.collected_data && Object.keys(c.collected_data).length > 0 ? Object.entries(c.collected_data).map(([k,v]) => `${k}: ${v}`).join(', ') : '-'}</td>
                      <td className="px-3 py-2">
                        {!['queued', 'initiating', 'connecting'].includes(c.status) && (
                          <button onClick={(e) => { e.stopPropagation(); handleRecall(c); }} className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded hover:bg-blue-100" data-testid={`recall-btn-${c.id}`}><Phone className="w-3 h-3 inline mr-1" />{tt('Перезвон')}</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50" data-testid="calls-pagination">
                <span className="text-xs text-gray-500">{page * perPage + 1}–{Math.min((page + 1) * perPage, callsData.total)} из {callsData.total}</span>
                <div className="flex gap-1">
                  <button onClick={() => setPage(0)} disabled={page === 0} className="px-2 py-1 text-xs border rounded hover:bg-white disabled:opacity-30">&#171;</button>
                  <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="px-2 py-1 text-xs border rounded hover:bg-white disabled:opacity-30">&#8249;</button>
                  <span className="px-3 py-1 text-xs font-medium">{page + 1} / {totalPages}</span>
                  <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="px-2 py-1 text-xs border rounded hover:bg-white disabled:opacity-30">&#8250;</button>
                  <button onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1} className="px-2 py-1 text-xs border rounded hover:bg-white disabled:opacity-30">&#187;</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // --- Stats Tab ---
  const StatsTab = () => (
    <div>
      <h3 className="text-lg font-semibold mb-4">{tt('Статистика')}</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 shadow-sm"><div className="text-sm text-gray-500">{tt('Всего звонков')}</div><div className="text-2xl font-bold text-blue-600">{stats.total_calls || 0}</div></div>
        <div className="bg-white rounded-xl p-4 shadow-sm"><div className="text-sm text-gray-500">{tt('Завершено')}</div><div className="text-2xl font-bold text-green-600">{stats.completed_calls || 0}</div></div>
        <div className="bg-white rounded-xl p-4 shadow-sm"><div className="text-sm text-gray-500">{tt('Заинтересованы')}</div><div className="text-2xl font-bold text-emerald-600">{stats.interested || 0}</div></div>
        <div className="bg-white rounded-xl p-4 shadow-sm"><div className="text-sm text-gray-500">{tt('Конверсия')}</div><div className="text-2xl font-bold text-purple-600">{stats.conversion_rate || 0}%</div></div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm"><div className="text-sm text-gray-500">{tt('Не ответили')}</div><div className="text-xl font-bold text-gray-500">{stats.no_answer || 0}</div></div>
        <div className="bg-white rounded-xl p-4 shadow-sm"><div className="text-sm text-gray-500">{tt('Скриптов')}</div><div className="text-xl font-bold text-blue-500">{stats.total_scripts || 0}</div></div>
      </div>
    </div>
  );

  // --- Settings Tab (per agent) ---
  const SettingsTab = () => {
    const profile = profiles.find(p => p.id === selectedProfileId) || {};
    const [profileForm, setProfileForm] = useState({
      name: profile.name || '', sip_server: profile.sip_server || '', sip_port: profile.sip_port || 5060,
      sip_username: profile.sip_username || '', sip_password: '', openai_api_key: '',
      dial_prefix: profile.dial_prefix || '', dial_prefix_plus: profile.dial_prefix_plus || false,
    });
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
      setSaving(true);
      try {
        await axios.put(`${API}/voice-agent/profiles/${selectedProfileId}`, profileForm, { headers: getHeaders() });
        toast.success(tt('Настройки сохранены'));
        fetchProfiles();
        fetchData();
      } catch (e) { toast.error(e.response?.data?.detail || 'Ошибка'); }
      setSaving(false);
    };

    const handleDelete = async () => {
      if (!window.confirm(`Удалить агента "${profile.name}"? Все скрипты и звонки сохранятся.`)) return;
      try {
        await axios.delete(`${API}/voice-agent/profiles/${selectedProfileId}`, { headers: getHeaders() });
        toast.success(tt('Агент удалён'));
        setSelectedProfileId(null);
        fetchProfiles();
      } catch (e) { toast.error(e.response?.data?.detail || 'Ошибка'); }
    };

    return (
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold">Настройки: {profile.name || 'Агент'}</h3>
          <button onClick={handleDelete} className="text-red-400 hover:text-red-600 text-sm flex items-center gap-1" data-testid="delete-agent-btn"><Trash2 className="w-4 h-4" /> {tt('Удалить агента')}</button>
        </div>
        <div className="mb-4 p-3 bg-blue-50 rounded-lg flex items-center gap-2 text-sm">
          <span className="text-blue-600 font-medium">{tt('IP сервера CRM:')}</span>
          <span className="font-mono text-blue-800 select-all">{settings.server_ip || '...'}</span>
          <span className="text-blue-400 ml-1">{tt('(добавьте в вайтлист телефонии)')}</span>
        </div>
        <div className="space-y-5">
          <div>
            <h4 className="font-medium text-gray-700 mb-3">{tt('Общие')}</h4>
            <div><label className="text-sm text-gray-500">{tt('Название агента')}</label><input className="w-full border rounded-lg px-3 py-2 mt-1" value={profileForm.name} onChange={e => setProfileForm({...profileForm, name: e.target.value})} /></div>
          </div>
          <div>
            <h4 className="font-medium text-gray-700 mb-3">{tt('SIP подключение')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><label className="text-sm text-gray-500">{tt('Сервер')}</label><input className="w-full border rounded-lg px-3 py-2 mt-1" value={profileForm.sip_server} onChange={e => setProfileForm({...profileForm, sip_server: e.target.value})} placeholder="91.92.246.170" /></div>
              <div><label className="text-sm text-gray-500">{tt('Порт')}</label><input type="number" className="w-full border rounded-lg px-3 py-2 mt-1" value={profileForm.sip_port} onChange={e => setProfileForm({...profileForm, sip_port: parseInt(e.target.value) || 5060})} /></div>
              <div><label className="text-sm text-gray-500">{tt('Логин')}</label><input className="w-full border rounded-lg px-3 py-2 mt-1" value={profileForm.sip_username} onChange={e => setProfileForm({...profileForm, sip_username: e.target.value})} /></div>
              <div><label className="text-sm text-gray-500">{tt('Пароль')}</label><input type="password" className="w-full border rounded-lg px-3 py-2 mt-1" placeholder={tt('(не изменять)')} value={profileForm.sip_password} onChange={e => setProfileForm({...profileForm, sip_password: e.target.value})} /></div>
            </div>
          </div>
          <div>
            <h4 className="font-medium text-gray-700 mb-3">{tt('Набор номера')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><label className="text-sm text-gray-500">{tt('Префикс набора')}</label><input className="w-full border rounded-lg px-3 py-2 mt-1" placeholder={tt('Например: 001 или 810')} value={profileForm.dial_prefix} onChange={e => setProfileForm({...profileForm, dial_prefix: e.target.value})} />
                <label className="flex items-center gap-2 mt-2 text-sm text-gray-600 cursor-pointer"><input type="checkbox" className="rounded" checked={profileForm.dial_prefix_plus} onChange={e => setProfileForm({...profileForm, dial_prefix_plus: e.target.checked})} /> {tt('Добавлять + перед набором')}</label>
              </div>
            </div>
          </div>
          <div>
            <h4 className="font-medium text-gray-700 mb-3">OpenAI API</h4>
            <div><label className="text-sm text-gray-500">API Key {profile.openai_api_key_set ? '(установлен)' : '(не установлен)'}</label>
            <input className="w-full border rounded-lg px-3 py-2 mt-1" value={profileForm.openai_api_key} onChange={e => setProfileForm({...profileForm, openai_api_key: e.target.value})} placeholder="sk-proj-..." type="password" /></div>
          </div>
          <button onClick={handleSave} disabled={saving} className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50" data-testid="save-settings-btn"><Save className="w-4 h-4" /> {tt('Сохранить настройки')}</button>
        </div>
      </div>
    );
  };

  // --- Campaign data fetching ---
  const campaignSearchTimer = React.useRef(null);

  const fetchCampaignClients = useCallback(async (search) => {
    setCampaignLoading(true);
    try {
      const p = {};
      if (campaignFilter.statuses?.length) p.status = campaignFilter.statuses.join(',');
      if (campaignFilter.campaigns?.length) p.campaign = campaignFilter.campaigns.join(',');
      if (campaignFilter.countries?.length) p.country = campaignFilter.countries.join(',');
      if (campaignFilter.managers?.length) p.assigned_manager = campaignFilter.managers.join(',');
      if (campaignFilter.ai_called) p.ai_called = campaignFilter.ai_called;
      const s = search !== undefined ? search : campaignSearch;
      if (s) p.search = s;
      p.limit = campaignFilter.limit || 500;
      const res = await axios.get(`${API}/voice-agent/campaign-preview`, { headers: getHeaders(), params: p });
      const cls = res.data.clients || [];
      setCampaignClients(cls);
      setCampaignTotal(res.data.total || 0);
      setSelectedClients(new Set());
    } catch (e) { /* */ }
    finally { setCampaignLoading(false); }
  }, [campaignFilter, campaignSearch]);

  // Auto-fetch when filters change and modal is open
  useEffect(() => {
    if (showCampaign) fetchCampaignClients();
  }, [showCampaign, campaignFilter, fetchCampaignClients]);

  const handleCampaignSearch = (val) => {
    setCampaignSearch(val);
    clearTimeout(campaignSearchTimer.current);
    if (val.length >= 2 || val.length === 0) {
      campaignSearchTimer.current = setTimeout(() => fetchCampaignClients(val), 400);
    }
  };

  const toggleCampaignClient = (id) => {
    setSelectedClients(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const toggleAllCampaign = () => {
    if (selectedClients.size === campaignClients.length && campaignClients.length > 0) {
      setSelectedClients(new Set());
    } else {
      setSelectedClients(new Set(campaignClients.map(c => c.id)));
    }
  };

  const handleCampaignStart = async () => {
    if (!campaignFilter.script_id) return toast.error(tt('Выберите скрипт'));
    if (selectedClients.size === 0) return toast.error(tt('Выберите клиентов'));
    try {
      setLoading(true);
      await axios.post(`${API}/voice-agent/campaign-start`, {
        script_id: parseInt(campaignFilter.script_id),
        client_ids: [...selectedClients],
      }, { headers: getHeaders() });
      setShowCampaign(false);
      setCampaignClients([]);
      setSelectedClients(new Set());
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Ошибка'); }
    finally { setLoading(false); }
  };

  const updateCampaignFilter = (key, val) => setCampaignFilter(prev => ({...prev, [key]: val}));

  // --- Campaign Modal Render ---
  const renderCampaignModal = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="campaign-modal">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <h3 className="text-lg font-semibold">{tt('Массовый обзвон')}</h3>
          <button onClick={() => { setShowCampaign(false); setSelectedClients(new Set()); }} data-testid="close-campaign"><X className="w-5 h-5 text-gray-400" /></button>
        </div>

        {/* Filters */}
        <div className="px-6 py-4 border-b bg-gray-50 space-y-3">
          <div>
            <label className="text-xs text-gray-500 font-medium uppercase">{tt('Скрипт')}</label>
            <select className="w-full border rounded-lg px-3 py-2 mt-0.5 text-sm" data-testid="campaign-script" value={campaignFilter.script_id} onChange={e => updateCampaignFilter('script_id', e.target.value)}>
              <option value="">{tt('Выберите скрипт')}</option>
              {scripts.filter(s => s.is_active).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
            <div>
              <label className="text-xs text-gray-500 font-medium">{tt('Статус')}</label>
              <CMultiSelect
                value={campaignFilter.statuses}
                onChange={v => updateCampaignFilter('statuses', v)}
                options={filterOptions.statuses.map(s => ({ value: s, label: STATUS_LABELS_RU[s] || s }))}
                placeholder={tt('Все')}
                testId="campaign-status-filter"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium">{tt('Кампания')}</label>
              <CMultiSelect
                value={campaignFilter.campaigns}
                onChange={v => updateCampaignFilter('campaigns', v)}
                options={filterOptions.campaigns.map(c => ({ value: c, label: c }))}
                placeholder={tt('Все')}
                testId="campaign-campaign-filter"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium">{tt('Страна')}</label>
              <CMultiSelect
                value={campaignFilter.countries}
                onChange={v => updateCampaignFilter('countries', v)}
                options={(filterOptions.countries || []).map(c => ({ value: c, label: c }))}
                placeholder={tt('Все')}
                testId="campaign-country-filter"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium">{tt('Сотрудник')}</label>
              <CMultiSelect
                value={campaignFilter.managers}
                onChange={v => updateCampaignFilter('managers', v)}
                options={(filterOptions.managers || []).map(m => ({ value: m.name, label: m.name }))}
                placeholder={tt('Все')}
                testId="campaign-manager-filter"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium">{tt('AI звонил')}</label>
              <select className="w-full border rounded-lg px-2 py-1.5 text-sm mt-0.5 bg-white" data-testid="campaign-ai-called-filter" value={campaignFilter.ai_called} onChange={e => updateCampaignFilter('ai_called', e.target.value)}>
                <option value="">{tt('Все')}</option>
                <option value="no">{tt('Не звонил')}</option>
                <option value="yes">{tt('Звонил')}</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2 text-gray-400" />
              <input className="w-full border rounded-lg pl-8 pr-3 py-1.5 text-sm" placeholder={tt('Поиск по имени или телефону...')} value={campaignSearch} onChange={e => handleCampaignSearch(e.target.value)} data-testid="campaign-search" />
            </div>
            <div className="flex items-center gap-1">
              <label className="text-xs text-gray-500 whitespace-nowrap">{tt('Лимит:')}</label>
              <input type="number" className="w-20 border rounded-lg px-2 py-1.5 text-sm" value={campaignFilter.limit} onChange={e => updateCampaignFilter('limit', parseInt(e.target.value) || 500)} min={1} max={5000} data-testid="campaign-limit" />
            </div>
            {(campaignFilter.statuses?.length > 0 || campaignFilter.campaigns?.length > 0 || campaignFilter.countries?.length > 0 || campaignFilter.managers?.length > 0 || campaignFilter.ai_called || campaignSearch) && (
              <button onClick={() => { setCampaignFilter(prev => ({...prev, statuses: [], campaigns: [], countries: [], managers: [], ai_called: ''})); setCampaignSearch(''); }} className="text-xs text-red-500 hover:text-red-700 whitespace-nowrap px-2 py-1.5 border border-red-200 rounded-lg hover:bg-red-50" data-testid="campaign-clear-filters">
                <X className="w-3 h-3 inline mr-0.5" />{tt('Сбросить')}
              </button>
            )}
          </div>
        </div>

        {/* Client list */}
        <div className="flex-1 overflow-hidden flex flex-col px-6 py-3">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-600">
              {campaignLoading ? 'Загрузка...' : <>{tt('Найдено:')} <b>{campaignTotal}</b> {tt('| Выбрано:')} <span className="font-bold text-blue-600">{selectedClients.size}</span></>}
            </span>
            <button onClick={toggleAllCampaign} className="text-xs text-blue-600 hover:text-blue-800" data-testid="campaign-toggle-all">
              {selectedClients.size === campaignClients.length && campaignClients.length > 0 ? 'Снять все' : 'Выбрать все'}
            </button>
          </div>
          <div className="flex-1 overflow-auto border rounded-lg max-h-[40vh]">
            {campaignClients.length === 0 ? (
              <div className="p-6 text-center text-gray-400 text-sm">{campaignLoading ? 'Загрузка клиентов...' : 'Нет клиентов по выбранным фильтрам'}</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr className="text-xs text-gray-500 uppercase">
                    <th className="w-8 px-2 py-2"></th>
                    <th className="text-left px-2 py-2">{tt('Клиент')}</th>
                    <th className="text-left px-2 py-2">{tt('Телефон')}</th>
                    <th className="text-left px-2 py-2">{tt('Страна')}</th>
                    <th className="text-left px-2 py-2">{tt('Сотрудник')}</th>
                    <th className="text-left px-2 py-2">AI</th>
                  </tr>
                </thead>
                <tbody>
                  {campaignClients.map(c => (
                    <tr key={c.id} className={`border-t hover:bg-gray-50 cursor-pointer ${selectedClients.has(c.id) ? '' : 'opacity-50'}`} onClick={() => toggleCampaignClient(c.id)}>
                      <td className="px-2 py-1.5 text-center"><input type="checkbox" checked={selectedClients.has(c.id)} readOnly className="rounded pointer-events-none" /></td>
                      <td className="px-2 py-1.5 font-medium">{c.name}</td>
                      <td className="px-2 py-1.5 text-gray-500 font-mono text-xs">{c.phone}</td>
                      <td className="px-2 py-1.5">{c.country ? <span className="text-[11px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{c.country}</span> : '-'}</td>
                      <td className="px-2 py-1.5 text-xs text-blue-500">{c.assigned_manager_name || '-'}</td>
                      <td className="px-2 py-1.5">
                        {c.ai_calls > 0
                          ? <span className="text-[11px] bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded" title={`Последний: ${c.last_ai_result || '?'}`}>{c.ai_calls} зв.</span>
                          : <span className="text-xs text-gray-300">-</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t">
          <button onClick={handleCampaignStart} disabled={loading || selectedClients.size === 0} className="w-full bg-green-600 text-white py-2.5 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2 font-medium" data-testid="campaign-start-btn">
            <Play className="w-4 h-4" /> {loading ? 'Запуск...' : `Запустить обзвон (${selectedClients.size})`}
          </button>
        </div>
      </div>
    </div>
  );

  // --- Guide Modal ---
  const GuideModal = () => (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-10 overflow-y-auto" onClick={() => setShowGuide(false)}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl m-4 mb-10" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold">{tt('Инструкция по подключению AI Агента')}</h2>
          <button onClick={() => setShowGuide(false)} className="text-gray-400 hover:text-gray-600" data-testid="close-guide"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-6 text-sm text-gray-700 leading-relaxed max-h-[70vh] overflow-y-auto">

          <section>
            <h3 className="font-bold text-base text-gray-900 mb-2">{tt('1. Что нужно для работы')}</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li><b>{tt('SIP-аккаунт')}</b> {tt('— от телефонного провайдера (например, Asterisk, Zadarma, Sipnet, любой VoIP)')}</li>
              <li><b>{tt('OpenAI API ключ')}</b> {tt('— для голосового ИИ (модель GPT-4o Realtime)')}</li>
              <li><b>{tt('Скрипт разговора')}</b> {tt('— шаблон что говорить клиенту')}</li>
            </ul>
          </section>

          <section>
            <h3 className="font-bold text-base text-gray-900 mb-2">{tt('2. Настройка SIP (Настройки)')}</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <p><b>{tt('SIP сервер')}</b> {tt('— IP адрес или домен вашей АТС (например:')} <code className="bg-gray-200 px-1 rounded">91.92.246.170</code>)</p>
              <p><b>{tt('SIP порт')}</b> {tt('— стандартный:')} <code className="bg-gray-200 px-1 rounded">5060</code> {tt('(менять только если провайдер указал другой)')}</p>
              <p><b>{tt('SIP логин')}</b> {tt('— номер внутренней линии (например:')} <code className="bg-gray-200 px-1 rounded">10705</code>)</p>
              <p><b>{tt('SIP пароль')}</b> {tt('— пароль от линии')}</p>
            </div>
          </section>

          <section>
            <h3 className="font-bold text-base text-gray-900 mb-2">{tt('3. Вайтлист IP (важно!)')}</h3>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="mb-2">{tt('Ваш SIP-провайдер может блокировать звонки с неизвестных IP. Нужно добавить IP сервера CRM в вайтлист провайдера.')}</p>
              <p><b>{tt('Текущий IP сервера:')}</b> {tt('смотрите в Настройках → "IP сервера CRM"')}</p>
              <p className="mt-2 text-yellow-800"><b>{tt('Внимание:')}</b> {tt('при обновлении/деплое CRM IP может измениться. После каждого деплоя проверяйте IP и обновляйте вайтлист.')}</p>
              <p className="mt-1 text-yellow-800">{tt('Рекомендация: попросите провайдера отключить IP-фильтрацию и оставить только авторизацию по логину/паролю.')}</p>
            </div>
          </section>

          <section>
            <h3 className="font-bold text-base text-gray-900 mb-2">{tt('4. OpenAI API ключ')}</h3>
            <ol className="list-decimal pl-5 space-y-1">
              <li>{tt('Зайдите на')} <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer" className="text-blue-600 underline">platform.openai.com/api-keys</a></li>
              <li>{tt('Создайте новый ключ (начинается с')} <code className="bg-gray-200 px-1 rounded">sk-...</code>)</li>
              <li>{tt('Вставьте в Настройки → "OpenAI API ключ"')}</li>
              <li>{tt('Убедитесь что на балансе OpenAI есть средства')}</li>
            </ol>
          </section>

          <section>
            <h3 className="font-bold text-base text-gray-900 mb-2">{tt('5. Создание скрипта')}</h3>
            <ol className="list-decimal pl-5 space-y-1">
              <li>{tt('Перейдите в')} <b>{tt('Скрипты')}</b> → <b>{tt('Новый скрипт')}</b></li>
              <li><b>{tt('Имя агента')}</b> {tt('— как AI представится (имя + фамилия)')}</li>
              <li><b>{tt('Должность')}</b> {tt('— роль в компании')}</li>
              <li><b>{tt('Компания')}</b> {tt('— название компании')}</li>
              <li><b>{tt('Приветствие')}</b> {tt('— первая фраза при звонке. Имя клиента добавится автоматически')}</li>
              <li><b>{tt('Голос')}</b> {tt('— выберите подходящий (ash — энергичный мужской, coral — женский)')}</li>
              <li><b>{tt('Макс. длительность')}</b> {tt('— лимит звонка в секундах (300 = 5 мин)')}</li>
            </ol>
          </section>

          <section>
            <h3 className="font-bold text-base text-gray-900 mb-2">{tt('6. Запуск звонка')}</h3>
            <ol className="list-decimal pl-5 space-y-1">
              <li>{tt('Убедитесь что статус')} <span className="text-green-600 font-semibold">{tt('На линии')}</span> {tt('(зелёный индикатор)')}</li>
              <li>{tt('Перейдите в')} <b>{tt('Звонки')}</b> → <b>{tt('Массовый обзвон')}</b></li>
              <li>{tt('Выберите скрипт и фильтры клиентов')}</li>
              <li>{tt('Нажмите')} <b>{tt('Начать обзвон')}</b></li>
            </ol>
          </section>

          <section>
            <h3 className="font-bold text-base text-gray-900 mb-2">{tt('7. Что делает AI')}</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>{tt('Звонит клиенту через SIP, говорит голосом OpenAI')}</li>
              <li>{tt('Следует скрипту: приветствие → суть → сбор информации → завершение')}</li>
              <li>{tt('Собирает данные: возраст, работа, банки, опыт инвестиций')}</li>
              <li>{tt('Результат и транскрипт сохраняются в карточке клиента')}</li>
              <li>{tt('При "перезвонить" или "заинтересован" — создаёт напоминание')}</li>
            </ul>
          </section>

          <section>
            <h3 className="font-bold text-base text-gray-900 mb-2">{tt('Решение проблем')}</h3>
            <div className="space-y-2">
              <p><b>{tt('"Не подключен"')}</b> {tt('→ Проверьте: 1) SIP данные верны 2) IP сервера в вайтлисте провайдера 3) OpenAI ключ активен')}</p>
              <p><b>{tt('Звонок не проходит')}</b> {tt('→ Проверьте баланс SIP-провайдера и формат номера телефона клиента')}</p>
              <p><b>{tt('AI молчит')}</b> {tt('→ Проверьте баланс OpenAI и что ключ начинается с')} <code className="bg-gray-200 px-1 rounded">sk-</code></p>
              <p><b>{tt('IP изменился')}</b> {tt('→ После деплоя зайдите в Настройки, скопируйте новый IP, обновите вайтлист')}</p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );

  const innerTabs = [
    { id: 'scripts', label: tt('Скрипты'), icon: FileText },
    { id: 'calls', label: tt('Звонки'), icon: Phone },
    { id: 'stats', label: tt('Статистика'), icon: BarChart3 },
    { id: 'settings', label: tt('Настройки'), icon: Settings },
  ];

  const selectedProfile = profiles.find(p => p.id === selectedProfileId);

  return (
    <div data-testid="voice-agent-page">
      {/* Agent tabs — top level */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-1 overflow-x-auto">
          {profiles.map(p => (
            <button key={p.id} onClick={() => { setSelectedProfileId(p.id); setTab('scripts'); setEditScript(null); }}
              className={`px-4 py-2 rounded-t-lg text-sm font-medium whitespace-nowrap transition-colors border-b-2 ${
                selectedProfileId === p.id ? 'bg-white border-blue-600 text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700 border-transparent hover:bg-gray-50'
              }`} data-testid={`agent-tab-${p.id}`}>
              {p.name || `Агент ${p.id}`}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowGuide(true)} className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-testid="guide-btn">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          </button>
          {creatingProfile ? (
            <div className="flex items-center gap-2">
              <input className="border rounded-lg px-3 py-1.5 text-sm w-40" placeholder={tt('Имя нового агента')} value={newProfileForm.name} onChange={e => setNewProfileForm({name: e.target.value})} autoFocus onKeyDown={e => e.key === 'Enter' && handleCreateProfile()} />
              <button onClick={handleCreateProfile} className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-green-700"><Plus className="w-4 h-4" /></button>
              <button onClick={() => setCreatingProfile(false)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <button onClick={() => setCreatingProfile(true)} className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-1.5 text-sm" data-testid="add-agent-btn">
              <Plus className="w-4 h-4" /> {tt('Агент')}
            </button>
          )}
        </div>
      </div>

      {selectedProfileId && (
        <>
          {/* Inner tabs + SIP status */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex gap-2 bg-gray-100 p-1 rounded-xl w-fit">
              {innerTabs.map(t => (
                <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t.id ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
                  <t.icon className="w-4 h-4" /> {t.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 text-sm" title={sipStatus?.connected ? sipStatus.response : (sipStatus?.error || 'Проверяется...')}>
              <span className={`w-2.5 h-2.5 rounded-full ${sipStatus === null ? 'bg-gray-300 animate-pulse' : sipStatus.connected ? 'bg-green-500' : 'bg-red-500'}`}></span>
              <span className={`${sipStatus?.connected ? 'text-green-700' : 'text-red-600'}`}>
                {sipStatus === null ? 'Проверка...' : sipStatus.connected ? 'На линии' : 'Не подключен'}
              </span>
              <button onClick={checkSip} className="text-gray-400 hover:text-gray-600 ml-1" title={tt('Проверить подключение')}>
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 11-6.22-8.56"/><path d="M21 3v6h-6"/></svg>
              </button>
            </div>
          </div>
          {tab === 'scripts' && <ScriptsTab />}
          {tab === 'calls' && <CallsTab />}
          {tab === 'stats' && <StatsTab />}
          {tab === 'settings' && <SettingsTab />}
        </>
      )}

      {!selectedProfileId && profiles.length === 0 && (
        <div className="bg-white rounded-xl p-12 text-center shadow-sm">
          <Phone className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-2">{tt('Нет агентов')}</h3>
          <p className="text-gray-400 mb-4">{tt('Создайте первого AI агента для начала работы')}</p>
          <button onClick={() => setCreatingProfile(true)} className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700" data-testid="create-first-agent">
            <Plus className="w-4 h-4 inline mr-2" /> {tt('Создать агента')}
          </button>
        </div>
      )}

      {showCampaign && renderCampaignModal()}
      {showGuide && <GuideModal />}
    </div>
  );
}
